import { useMemo, useState } from 'react';
import { irfConfig } from '../IrfConfig';
import { InternalIrfPublicationProps } from './fetchData';
import { extractTypeCodes, filterByTypeCode } from '../index';

export interface TabState {
  publicationTab: InternalIrfPublicationProps[];
  projectTab: InternalIrfPublicationProps[];
  presentationTab: InternalIrfPublicationProps[];
  supervisedThesesTab: InternalIrfPublicationProps[];
  othersTab: InternalIrfPublicationProps[];
}

export const useTabs = (
  jsonData: InternalIrfPublicationProps[],
  selectedTypes: string[] | null,
  person: string | null,
) => {
  const selectedPublicationTypes = extractTypeCodes(selectedTypes || []);

  const tabs = useMemo(() => {
    // Get all publications for the publication tab (excluding type 11)
    const publicationsWithoutTheses = filterByTypeCode(
      jsonData,
      selectedPublicationTypes,
      irfConfig.publicationTab.filter((code) => code !== '11'),
    );

    // Get all supervised theses (type 11)
    const supervisedTheses = filterByTypeCode(
      jsonData,
      selectedPublicationTypes,
      ['11'],
    );

    /**
     * Helper function to check if a person is related to a thesis
     * Checks both authors array and citation entry for person's name
     * Handles different name formats (Last, First vs First Last)
     */
    const isPersonRelatedToThesis = (
      entry: InternalIrfPublicationProps,
      person: string,
    ): boolean => {
      if (!person) return false;

      // Normalize person name for comparison
      const normalizedPerson = person.toLowerCase().trim();

      // Check authors array with exact match first
      const isInAuthorsExact = entry.authors?.includes(person) ?? false;
      if (isInAuthorsExact) return true;

      // Check authors array with normalized comparison for format variations
      const isInAuthorsNormalized =
        entry.authors?.some((author) => {
          const normalizedAuthor = author.toLowerCase().trim();

          // Direct match
          if (normalizedAuthor === normalizedPerson) return true;

          // Handle "Last, First" vs "First Last" format
          const personParts = normalizedPerson.split(/[,\s]+/).filter(Boolean);
          const authorParts = normalizedAuthor.split(/[,\s]+/).filter(Boolean);

          // If both have at least 2 parts, check if they contain the same name parts
          if (personParts.length >= 2 && authorParts.length >= 2) {
            return (
              personParts.every((part) => authorParts.includes(part)) &&
              authorParts.every((part) => personParts.includes(part))
            );
          }

          return false;
        }) ?? false;

      if (isInAuthorsNormalized) return true;

      // Check citation entry with exact match first
      const isInCitationExact = entry.citation_entry?.includes(person) ?? false;
      if (isInCitationExact) return true;

      // Check citation entry with normalized comparison
      const isInCitationNormalized =
        entry.citation_entry?.toLowerCase().includes(normalizedPerson) ?? false;

      return isInCitationNormalized;
    };

    // If person is specified, split theses based on authorship/mention
    let personAuthoredTheses: InternalIrfPublicationProps[] = [];
    let supervisedByPersonTheses: InternalIrfPublicationProps[] = [];

    if (person) {
      // Extract person's lastName for citation matching
      const personParts = person.split(/[,\s]+/).filter(Boolean);
      const personLastName = personParts[0]?.toLowerCase().trim();

      supervisedTheses.forEach((entry: InternalIrfPublicationProps) => {
        // Check if person is related to thesis (author or mentioned in citation)
        const isPersonRelated = isPersonRelatedToThesis(entry, person);

        if (isPersonRelated) {
          // Case 1: Person is author or mentioned - goes to Publications tab
          personAuthoredTheses.push(entry);
        } else {
          // Person not related - check additional cases

          // Case 2: Check if person's lastName appears in citation
          const citationLower = entry.citation_entry?.toLowerCase() || '';
          const hasPersonLastNameInCitation =
            personLastName && citationLower.includes(personLastName);

          // Case 3: Check if SINGLE author appears in citation (original logic)
          // BUT make it more restrictive - only if it's NOT a student thesis OR has additional criteria
          const hasAuthorInCitation =
            (entry.authors?.length === 1 &&
              entry.authors?.some((author) => {
                if (!author || !entry.citation_entry) return false;

                const authorParts = author
                  .split(',')
                  .map((part) => part.trim());
                if (authorParts.length < 2) return false;

                const lastName = authorParts[0].toLowerCase();
                const firstInitial = authorParts[1].toLowerCase()[0];

                // Check if author appears in citation (e.g., "Guasso, Chiara" -> "guasso, c.")
                return citationLower.includes(`${lastName}, ${firstInitial}.`);
              })) ??
            false;

          // More restrictive logic: Only move to Publications if:
          // 1. Person's lastName in citation, OR
          // 2. Author in citation AND it's peer reviewed OR invented_here is true
          if (
            hasPersonLastNameInCitation ||
            (hasAuthorInCitation &&
              (entry.peer_reviewed || entry.invented_here === false))
          ) {
            // Goes to Publications tab
            personAuthoredTheses.push(entry);
          } else {
            // All other cases - goes to Supervised Theses tab
            // This will include most type 11 entries that don't match person's lastName
            supervisedByPersonTheses.push(entry);
          }
        }
      });
    } else {
      // No person selected - all theses go to supervised theses tab
      supervisedByPersonTheses = supervisedTheses;
    }

    return {
      publicationTab: [
        ...publicationsWithoutTheses,
        ...personAuthoredTheses, // Add theses where person is author or mentioned in citation to publicationTab
      ],
      projectTab: filterByTypeCode(
        jsonData,
        selectedPublicationTypes,
        irfConfig.projectTab,
      ),
      presentationTab: filterByTypeCode(
        jsonData,
        selectedPublicationTypes,
        irfConfig.presentationTab,
      ),
      supervisedThesesTab: supervisedByPersonTheses, // Contains theses supervised by person (not authored/mentioned)
      othersTab: filterByTypeCode(
        jsonData,
        selectedPublicationTypes,
        irfConfig.othersTab,
      ),
    };
  }, [person, jsonData, selectedPublicationTypes]);

  const [activeTab, setActiveTab] = useState<keyof TabState>(() => {
    // If person is selected and has authored theses or sole-supervised theses, default to publicationTab
    if (person && jsonData.some((entry) => entry.authors?.includes(person))) {
      return 'publicationTab';
    }

    // Otherwise use first tab with data
    const firstTabWithData = Object.entries(tabs).find(
      ([_, entries]) => entries.length > 0,
    );
    return (firstTabWithData?.[0] as keyof TabState) || 'publicationTab';
  });

  return { tabs, activeTab, setActiveTab };
};

export const useActiveTabs = (
  tabs: TabState,
  showOnlyPeerReviewed: boolean,
  excludedPublications: string[] = [],
) => {
  return useMemo(
    () =>
      Object.entries(tabs).filter(([tabKey, entries]) => {
        const filteredEntries = entries.filter(
          (entry: InternalIrfPublicationProps) =>
            !excludedPublications.includes(entry.url_handle?.toString() || ''),
        );
        return (
          filteredEntries.length > 0 &&
          (tabKey === 'projectTab' ||
            !showOnlyPeerReviewed ||
            filteredEntries.some(
              (entry: InternalIrfPublicationProps) => entry.peer_reviewed,
            ))
        );
      }),
    [tabs, showOnlyPeerReviewed, excludedPublications],
  );
};
